<template>
  <div ref="container" style="min-height: 250px;">
    <tip name="timelineWelcome" wrapper-class="q-pa-md">
      <q-item-label class="text-bold">
        Activity Timeline
      </q-item-label>
      <q-item-label>
        The Activity Timeline collates all your {{ modelName }} data together.
      </q-item-label>
      <articles
        :types="['ARTICLE', 'YOUTUBE']"
        :limit="5"
        search-term="timeline"
      >
        <q-item-label>
          Use the following articles to learn how to use it.
        </q-item-label>
      </articles>
    </tip>

    <AddNoteOrFile
      v-if="!formattedReadOnly.add"
      :disable="loading"
      :hide-add-files="disableAddFiles"
      :hide-add-notes="disableAddNotes"
      :file-entries="fileEntries"
      wrapper-class="q-pa-md q-gutter-md"
      @add-note="(payload, done) => $emit('addNote', payload, done)"
      @save-attachments="(idsToUse, filesToRemove, done) => $emit('saveAttachments', idsToUse, filesToRemove, done)"
    />

    <div class="row reverse-md">
      <!-- Sidebar -->
      <div
        class="col-12"
        :class="{'col-md-4': !hideTiles && !manual}"
      >
        <div class="q-pa-md full-height">
          <TileGroup
            v-if="!manual && !hideTiles && Object.keys(tilesAsObject).length > 0"
            v-model:selected-partial-types="selectedPartialTypes"
            class="q-mb-md"
            :tiles="tilesAsObject"
            @update:selected-partial-types="resetVirtualScroller"
          />

          <div class="sticky-position" :class="{'q-pt-md': !manual && !hideTiles, ...anchorClasses}">
            <div class="row items-center q-col-gutter-sm">
              <div class="col-12" :class="{'col-sm-8': manual && hasSearchSlot}">
                <q-input
                  v-model="search"
                  dense
                  placeholder="Search Entries..."
                  outlined
                  clearable
                  :debounce="1000"
                  @update:model-value="onInputSearch"
                >
                  <template #prepend>
                    <q-icon name="mdi-magnify" />
                  </template>
                </q-input>
              </div>
              <div
                v-if="hasSearchSlot"
                class="col-12"
                :class="{'col-sm-4': manual}"
              >
                <slot name="search" />
              </div>
            </div>

            <q-card v-if="!manual && !hideTiles" class="q-mt-md gt-sm">
              <q-card-section v-if="hasCollatedData" class="text-body2 text-bold">
                Filters
              </q-card-section>
              <q-item-wrapper
                v-if="hasCollatedData"
                class="q-mb-md"
                tag="label"
                label="Show Collated Entries"
                :caption="`${showCollatedEntries ? 'Collated entries are highlighted. They' : 'Collated entries'} are entries which indirectly relate to this ${modelName}.`"
                dense
                @update:model-value="resetVirtualScroller"
              >
                <template #before>
                  <q-item-section side>
                    <q-checkbox v-model="showCollatedEntries" />
                  </q-item-section>
                </template>
              </q-item-wrapper>

              <q-card-section class="text-body2 text-bold">
                Filter by Date Range
              </q-card-section>
              <q-date
                ref="rangePicker"
                v-model="dateRange"
                :events="pickerDates"
                :default-year-month="closestToToday"
                :first-day-of-week="startOfWeek"
                range
                minimal
                flat
                class="full-width q-date--without-padding q-pa-sm"
              >
                <q-btn
                  v-if="!!dateRange"
                  dense
                  label="Clear"
                  flat
                  @click="dateRange = null"
                />
              </q-date>
            </q-card>
          </div>
        </div>
      </div>

      <!-- Timeline -->
      <div class="col-12" :class="{'col-md-8': !hideTiles && !manual, 'flex flex-center': groupedEntriesPaged.length < 1}">
        <q-timeline
          v-if="!loading && groupedEntriesPaged.length > 0"
          class="q-pl-sm timeline-container"
          :class="{'read-only': formattedReadOnly.view, ...anchorClasses}"
        >
          <!-- Pinned/Dates + Entries -->
          <q-virtual-scroll
            ref="virtualScroll"
            key="entries"
            :items="groupedEntriesPaged"
            :virtual-scroll-slice-size="2"
            :scroll-target="scrollTargetEl"
          >
            <template #default="{ item: obj, index }">
              <TimelineGroup
                :key="obj.date"
                :date="obj.date"
                :entries="obj.entries"
                :read-only="formattedReadOnly.view"
                :disable-pinning="disablePinning"
                :partial-types="partialTypes"
                :big-data-mode="index >= 100"
                :manual="manual"
                @remove="removeEntry"
                @save="saveEntry"
                @pin="pinEntry"
              />
            </template>
          </q-virtual-scroll>
        </q-timeline>

        <div
          v-if="!manual && groupedEntries.length > groupedEntriesPaged.length"
          class="q-pa-md"
        >
          <q-btn
            label="Show Older Results..."
            no-caps
            rounded
            color="blue-grey-7"
            class="full-width"
            @click="datePage++"
          />
        </div>

        <loading-spinner v-if="loading" />
        <no-results
          v-else-if="groupedEntriesPaged.length < 1"
          title="No entries to display"
          class="self-center"
        />

        <!-- Back to top button -->
        <transition name="fadeTransition" mode="out-in">
          <q-toolbar
            v-if="!manual && !hideTiles && showBackToTop"
            class="sticky-position from-page-bottom justify-center blurred-container"
          >
            <q-btn
              label="Back To Top"
              no-caps
              color="blue-7"
              icon="mdi-chevron-up"
              rounded
              size="12px"
              @click="scrollToTop(0)"
            />
          </q-toolbar>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import TimelineGroup from 'components/partials/timeline/partials/TimelineGroup'
import TileGroup from 'components/partials/timeline/partials/TileGroup'
import AddNoteOrFile from 'components/partials/timeline/partials/AddNoteOrFile'

import timelineCommonMixin from 'components/partials/timeline/partials/timelineCommonMixin'
import { stickyValidator, stickyClasses } from 'src/helpers/sticky'

/**
 * Timeline
 *
 * The timeline is used for grouping together many different models and displaying them on a calendar-formatted timeline
 *
 * SLOTS:
 * - search: additional search content
 *
 * EMITS/EVENTS:
 * ##### CALL doneFunc() ONCE REQUEST IS COMPLETED (WHERE APPLICABLE)
 *
 * - addNote (note, doneFumc): add a new note to the timeline and submit to backend
 * - updateNote (updatedNote, doneFunc): update an existing note and submit to backend
 * - removeNote (id): Remove a note from the timeline and submit to backend
 * - pinNote (id, pinState, doneFunc): pin/unpin note from the timeline and submit to backend
 *
 * - saveAttachments (ids): IDs of files to save to this timeline and submit to backend
 * - removeAttachment (id): Remove a single attachment from the timeline and submit to backend
 * - pinAttachment (id, pinState, doneFunc): pin/unpin a single attachment from the timeline and submit to backend
 *
 * - search (val): If manual search is enabled, will emit search val to parent component
 */
export default {
  components: {
    AddNoteOrFile, TileGroup, TimelineGroup
  },
  mixins: [
    timelineCommonMixin
  ],
  props: {
    // Timeline arrau from backend
    timeline: {
      type: Array
    },
    // Array of tiles which can be shown on this timeline
    // Optionally define as an object to display a tooltip against each key
    // ie: note (key): "The timeline will display all collated client and appointment notes" (tooltip value)
    tiles: {
      type: [Array, Object],
      required: true
    },
    // Force read-only over all elements on the timeline
    // true, false, or string (none, add, view, all)
    readOnly: {
      type: [String, Boolean],
      default: false
    },
    // if manually handled, the backend is in charge of fetching data
    manual: {
      type: Boolean,
      default: false
    },
    // Hide the category tiles at the top of the timeline
    hideTiles: {
      type: Boolean,
      default: false
    },
    // Container anchor (either: none [disable sticky], dialog(-menubar) [parent is a dialog], page(-menubar) [parent is a page]
    anchor: {
      type: String,
      validator: stickyValidator,
      default: 'dialog'
    },
    // Default scroll target for the timeline
    scrollTarget: {
      type: String,
      default: '.content-card'
    },
    // The name of the model this timeline is for
    modelName: {
      type: String,
      default: 'item'
    },
    // Config / props to bind to each specific type
    config: {
      type: Object,
      default: () => {
        return {}
      }
    },
    // Disable button for adding new files to the timeline
    disableAddFiles: {
      type: Boolean,
      default: false
    },
    // Disable button for adding new note to the timeline
    disableAddNotes: {
      type: Boolean,
      default: false
    },
    // Disable pinning entries but don't treat element as read-only
    disablePinning: {
      type: Boolean,
      default: false
    },
    // Is the timeline loading
    loading: {
      type: Boolean,
      default: false
    },
    // Default permission to apply to note, file, log, communication tiles
    // Setting a permission will hide these tiles if the user doesn't have access
    defaultTilePermission: {
      type: String,
      default: null
    }
  },
  emits: ['addNote', 'saveAttachments', 'search'],
  data () {
    return {
      datePage: 1,
      scrollPos: 0,
      search: null,
      dateRange: null,
      showCollatedEntries: true,
      // All available categories/models which can be shown on the timeline
      partialTypes: this.basePartialTypes(this.config, this.defaultTilePermission),
      selectedPartialTypes: []
    }
  },
  computed: {
    startOfWeek () {
      return this.$store.getters['company/getCompany'].calendar_start_of_week || 0
    },
    scrollTargetEl () {
      try {
        return this.$refs.container.closest(this.scrollTarget)
      }
      catch (err) {
        return undefined
      }
    },
    showBackToTop () {
      return this.scrollPos > 1000
    },
    insensitiveSearch () {
      return this.search ? this.search.toLowerCase() : null
    },
    anchorClasses () {
      return stickyClasses(this.anchor)
    },
    // Go through all entries and add to any relevant data arrays
    formattedEntries () {
      // Group by date
      const dateObj = {}
      // Get the minimum height for each date section
      const dateHeights = {}
      // List of all dates to show on DatePicker
      const pickerDates = []
      const categoryEntries = {}
      const pinned = []
      let pinnedHeight = 0
      let hasCollatedData = false
      // Set the default date for the date range picker. Default to today unless entries exist
      let closestToToday = null
      const files = []
      const now = this.luxon.now()

      let dateRange = this.dateRange
      if (typeof dateRange === 'string') {
        dateRange = { to: dateRange, from: dateRange }
      }
      const startDate = dateRange && dateRange.from ? this.luxon.fromSQL(dateRange.from.replaceAll('/', '-')).startOf('day') : null
      const endDate = dateRange && dateRange.to ? this.luxon.fromSQL(dateRange.to.replaceAll('/', '-')).endOf('day') : null

      // Set up initial array for each category
      for (const ele of this.partialTypes) {
        categoryEntries[ele.value] = []
      }

      // Main loop for splitting up the timeline entries appropriately
      for (const ele of (this.timeline || [])) {
        if (ele.timeline_type === 'attachment' && ele.key === 'attachments') {
          files.push(ele)
        }

        if (!hasCollatedData && !!ele.timeline_morph) {
          hasCollatedData = true
        }

        // Only for data which should be visible
        // Should be part of a selected category + meet search criteria
        // Must replace space with T for date to be recognised in Safari
        const date = this.luxon.fromSQL(ele.timeline_date)
        const type = this.partialTypes.find(item => item.value === ele.timeline_type)

        // If the string contains only a date, we don't want to convert to company timezone
        const formattedDate = ele.timeline_date.length <= 10
          ? ele.timeline_date
          : this.luxon.fromSQL(ele.timeline_date).toSQLDate()

        const inSelectedTypes = this.selectedPartialTypes.includes(ele.timeline_type)
        const inSearchOptions = this.manual || !this.search || this.search.length < 3 || (type && type.searchHandler && type.searchHandler(ele, this.insensitiveSearch))
        const inDateRangeOptions = !startDate || !endDate || (date >= startDate && date <= endDate)
        const canDisplayIfMorphed = this.showCollatedEntries || !ele.timeline_morph

        if (canDisplayIfMorphed && inSearchOptions) {
          if (inDateRangeOptions) {
            categoryEntries[ele.timeline_type].push(ele)
          }

          if (inSelectedTypes) {
            if (!pickerDates.includes(formattedDate)) {
              pickerDates.push(formattedDate)
            }

            if (inDateRangeOptions) {
              if (ele.timeline_pinned) {
                pinned.push(ele)
                pinnedHeight += type.minHeight || 32
              }
              else {
                if (!dateObj[formattedDate]) {
                  dateHeights[formattedDate] = 0
                  dateObj[formattedDate] = []
                }
                dateHeights[formattedDate] += type.minHeight || 32
                dateObj[formattedDate].push(ele)
              }
            }
          }
        }
      }

      // Take this final object and convert to array for use by VirtualScroll
      // This loop is only for visible data which need to be grouped
      // Loop will also find the closest date to today's date for setting the default date on the date picker
      const dateArr = []
      let index = 0
      for (const date in dateObj) {
        const luxonDate = this.luxon.fromSQL(date)
        if (luxonDate < now && (!closestToToday || luxonDate > closestToToday)) {
          closestToToday = luxonDate
        }

        dateArr.push({
          date,
          entries: dateObj[date],
          minHeight: index >= 100 ? 60 : dateHeights[date]
        })
        index++
      }

      // If user is only looking at future appointments, we want to show the closest appointments at the top
      const sortAscending = this.selectedPartialTypes.length === 1 && this.selectedPartialTypes.includes('appointment_future')
      dateArr.sort((a, b) => {
        const luxonA = this.luxon.fromSQL(a.date)
        const luxonB = this.luxon.fromSQL(b.date)
        if (sortAscending) {
          return luxonA > luxonB ? 1 : -1
        }
        return luxonB > luxonA ? 1 : -1
      })

      if (pinned.length > 0) {
        dateArr.unshift({
          date: 'pinned',
          entries: pinned,
          minHeight: pinnedHeight
        })
      }

      return {
        entries: dateArr,
        closestToToday: closestToToday ? closestToToday.toSQLDate() : now.toSQLDate(),
        files,
        pickerDates,
        categoryEntries,
        hasCollatedData
      }
    },
    // Does the timeline contain any collated data
    hasCollatedData () {
      return this.formattedEntries.hasCollatedData
    },
    // Timeline entries grouped by date (by company timezone)
    groupedEntries () {
      return this.formattedEntries.entries
    },
    groupedEntriesPaged () {
      if (this.manual) {
        return this.groupedEntries
      }
      return this.groupedEntries.slice(0, this.datePage * 20)
    },
    // Calculate the total min height for the timeline
    minTimelineHeight () {
      return this.groupedEntriesPaged.reduce((a, b) => a + b.minHeight, 0)
    },
    // Any files. Necessary for pushing and removing any attachments from this timeline
    fileEntries () {
      return this.formattedEntries.files
    },
    // Array of all dates on the timeline
    pickerDates () {
      // Quasar QDate requires dates to use / instead of -
      return this.formattedEntries.pickerDates.map(item => item.replaceAll('-', '/'))
    },
    // All the objects grouped by the category they belong in
    entriesByCategory () {
      return this.formattedEntries.categoryEntries
    },
    closestToToday () {
      return this.luxon.fromSQL(this.formattedEntries.closestToToday).toFormat('yyyy/LL')
    },
    hasSearchSlot () {
      return !!this.$slots.search
    },
    formattedReadOnly () {
      switch (this.readOnly) {
        case 'add':
          return { add: true, view: false }
        case 'view':
          return { add: false, view: true }
        case true:
        case 'all':
          return { add: true, view: true }
        case false:
        default:
          return { add: false, view: false }
      }
    },
    // Get all the details for all the visible tiles
    // If user is permission-restricted, some tiles will be disabled
    tilesAsObject () {
      let iterator = {}
      if (!Array.isArray(this.tiles)) {
        iterator = JSON.parse(JSON.stringify(this.tiles))
      }
      else {
        for (const ele of this.tiles) {
          iterator[ele] = true
        }
      }

      const obj = {}
      for (const ele in iterator) {
        const tileDetails = this.partialTypes.find(item => item.value === ele)
        if (!tileDetails || (tileDetails.permission && !this.can(tileDetails.permission))) {
          continue
        }
        obj[ele] = {
          tooltip: typeof iterator[ele] === 'string' ? iterator[ele] : null,
          count: (this.entriesByCategory[ele] || []).length,
          details: tileDetails,
          entries: this.entriesByCategory[ele] || []
        }
      }
      return obj
    }
  },
  watch: {
    // If the timeline data changes
    'timeline' () {
      if (!this.dateRange && !this.hideTiles && !this.manual) {
        this.$refs.rangePicker.setCalendarTo(...this.closestToToday.split('/'))
      }
    },
    'dateRange' () {
      this.resetVirtualScroller()
    }
  },
  methods: {
    resetVirtualScroller () {
      if (this.$refs.virtualScroll) {
        this.$refs.virtualScroll.reset()
      }
    },
    // Scroll to top of container
    scrollToTop (val = 0) {
      setTimeout(() => {
        try {
          this.scrollTargetEl.scrollTop = val
        }
        catch (err) {}
      }, 0)
    },
    // On container scroll, set the current scroll position
    onScroll () {
      try {
        this.scrollPos = Math.floor(this.scrollTargetEl.scrollTop)
      }
      catch (err) {}
    },
    onInputSearch (val) {
      if (!this.manual) {
        this.datePage = 1
        return
      }
      this.resetVirtualScroller()
      this.$emit('search', val)
    },
    // Request saveHandler if an entry is updated
    saveEntry (obj) {
      this.baseSaveEntry(this.partialTypes, obj)
    },
    // Request pinHandler if an entry is pinned/unpinned
    pinEntry (obj) {
      this.basePinEntry(this.partialTypes, obj)
    },
    // Request removeHandler if an entry is removed from the timeline
    removeEntry (obj, silent = false) {
      this.baseRemoveEntry(this.partialTypes, this.timeline, obj, silent)
    }
  },
  mounted () {
    // Select default partial types when component is first loaded
    this.selectedPartialTypes = Object.keys(this.tilesAsObject)

    try {
      this.scrollTargetEl.addEventListener('scroll', this.onScroll)
    }
    catch (err) {}
  },
  beforeUnmount () {
    try {
      this.scrollTargetEl.removeEventListener('scroll', this.onScroll)
    }
    catch (err) {}
  }
}
</script>
